import React, { useState, useEffect } from "react";
import { View, Text, Image, TouchableOpacity, ActivityIndicator, Dimensions } from "react-native";
import { AppImages } from "../../utility/AppImages";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { LinearGradient } from "expo-linear-gradient";
import { encode } from "js-base64";
import { developUrl } from "../../utility/Index";
import { getReferralSession } from "../../services/authAPI";
import { getLoginData } from "../../utility/AppStoreData";
import { toast } from "react-toastify";
import * as Clipboard from "expo-clipboard";
import { useLiveStream } from "../../context/LiveStreamContext";

interface StreamSharePopupProps {
  onClose: () => void;
}

const StreamSharePopup: React.FC<StreamSharePopupProps> = ({ onClose }) => {
  const { currentEventId } = useLiveStream();
  const [showLoader, setShowLoader] = useState(true);
  const [referralData, setReferralData] = useState<any>(null);

  // Responsive values based on screen width
  const { width: screenWidth } = Dimensions.get("window");
  const isUltraWide = screenWidth >= 2000;
  const iconSize = isUltraWide ? 48 : 32;
  const iconGap = isUltraWide ? 25 : 12;
  const horizontalPadding = isUltraWide ? 20 : 12;

  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        setShowLoader(true);
        const data = await getLoginData();
        if (data != null && data.AccessToken) {
          const token = data.AccessToken;
          const response = await getReferralSession(token);
          setReferralData(response);
        } else {
          console.error("No access token found");
        }
      } catch (error) {
        console.error("Error fetching referral data:", error);
      } finally {
        setShowLoader(false);
      }
    };

    fetchReferralData();
  }, []);

  const getShareUrl = () => {
    let baseUrl = `${developUrl}/GameShowScreen?eventID=${currentEventId}`;

    if (referralData) {
      const inviteParams = encode(
        JSON.stringify({
          referralCode: referralData.referralCode,
          directReferrerId: referralData.directReferrerId,
        })
      );
      return `${baseUrl}&invite=${inviteParams}`;
    }

    return baseUrl;
  };

  const shareOnPlatform = (platform: string) => {
    const url = getShareUrl();
    let shareUrl = "";

    switch (platform) {
      case "Whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(url)}`;
        break;
      case "Instagram":
        shareUrl = `instagram://sharesheet?text=${encodeURIComponent(url)}`;
        break;
      case "Twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`;
        break;
      case "Facebook":
        shareUrl = `fb-messenger://share?link=${encodeURIComponent(url)}`;
        break;
      default:
        break;
    }
    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  const copyToClipboard = async () => {
    try {
      const linkToCopy = getShareUrl();
      await Clipboard.setStringAsync(linkToCopy);
      toast.success("Link copied to clipboard");
      onClose();
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  const socialMediaOptions = [
    { id: 1, name: "Instagram", image: AppImages.icSocialInsta, platform: "Instagram" },
    { id: 2, name: "Whatsapp", image: AppImages.icSocialWhatsapp, platform: "Whatsapp" },
    { id: 3, name: "Twitter", image: AppImages.icSocialTwitter, platform: "Twitter" },
    { id: 4, name: "Facebook", image: AppImages.icSocialFacebook, platform: "Facebook" },
  ];

  return (
    <LinearGradient
      colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#374151",
        padding: 12,
        height: "100%",
        justifyContent: "center",
      }}
    >
      {showLoader ? (
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ActivityIndicator size="large" color={AppColors.koBrandRed} />
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_14,
              { color: "white", marginTop: 12 },
            ]}
          >
            Preparing Invite...
          </Text>
        </View>
      ) : (
        <>
          {/* Header */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text style={[AppFontStyle.BOLD_ANYBODY_16, { color: "white" }]}>
              Invite friends
            </Text>
            <TouchableOpacity onPress={onClose}>
              <Image
                source={AppImages.icClose}
                style={{ width: 18, height: 18, tintColor: "white" }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>

          {/* Social Media Icons */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "center",
              gap: iconGap,
              marginBottom: 12,
              paddingHorizontal: horizontalPadding,
            }}
          >
            {socialMediaOptions.map((option, index) => (
              <TouchableOpacity
                key={index}
                onPress={() => shareOnPlatform(option.platform)}
                style={{ alignItems: "center" }}
              >
                <View
                  style={{
                    width: iconSize,
                    height: iconSize,
                    borderRadius: 10,
                    overflow: "hidden",
                    marginBottom: 4,
                  }}
                >
                  <Image
                    source={option.image}
                    style={{ width: iconSize, height: iconSize }}
                    resizeMode="cover"
                  />
                </View>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_8,
                    { color: "white", textAlign: "center" },
                  ]}
                >
                  {option.name}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Link Section */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#1F2937",
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "#374151",
              paddingHorizontal: 12,
              paddingVertical: 8,
            }}
          >
            <Image
              source={AppImages.icLinkSocial}
              style={{ width: 16, height: 16, marginRight: 8 }}
              resizeMode="contain"
            />
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                { color: "white", flex: 1 },
              ]}
              numberOfLines={1}
            >
              {getShareUrl()}
            </Text>
            <TouchableOpacity
              onPress={copyToClipboard}
              style={{
                backgroundColor: AppColors.koBrandRed,
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 4,
                marginLeft: 8,
              }}
            >
              <Text style={[AppFontStyle.BOLD_ANYBODY_10, { color: "white" }]}>
                Copy link
              </Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </LinearGradient>
  );
};

export default StreamSharePopup;
